import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Lock, Unlock, RotateCcw } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Problem statements for a hackathon, end to end.
 *
 * The pieces existed but were scattered: a shortlist offered from inside one
 * team's modal, a limit per problem that nothing displayed, and an allocation
 * across every team that had no screen at all. An organiser could act on one
 * team without ever seeing the shape of the whole thing.
 *
 * Here the whole thing is the point. How thinly the problems are spread, what
 * each team has been offered and chosen, and the two bulk actions — offer to
 * everyone, or put everyone back to nothing — sit on one screen.
 */

interface HackathonOption { _id: string; title: string }

interface Usage {
  statementId: string;
  title: string;
  assignedTo: string[];
  chosenBy: string[];
  offeredTo: string[];
}

interface TeamRow {
  _id: string;
  name: string;
  status: string;
  offeredProblemStatements?: { statementId: string; title: string }[];
  selectedProblemStatement?: { title: string };
  assignedProblemStatement?: { title: string };
  problemStatementLockedAt?: string;
}

export default function ProblemStatementsPanel() {
  const { success, error } = useToast();
  const [hackathons, setHackathons] = useState<HackathonOption[]>([]);
  const [hackathonId, setHackathonId] = useState('');
  const [usage, setUsage] = useState<Usage[]>([]);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [perTeam, setPerTeam] = useState(3);
  const [cap, setCap] = useState(6);
  const [preview, setPreview] = useState<{ team: string; statements: string[] }[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/hackathons').then((r) => {
      const list = r.data?.data || r.data?.hackathons || r.data || [];
      setHackathons(Array.isArray(list) ? list : []);
    }).catch(() => setHackathons([]));
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) { setUsage([]); setTeams([]); return; }
    try {
      const [u, t] = await Promise.all([
        api.get(`/teams/admin/problem-usage/${id}`),
        api.get(`/teams/hackathon/${id}`)
      ]);
      setUsage(u.data.statements || []);
      setTeams((t.data || []).filter((x: TeamRow) => x.status !== 'dissolved'));
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not load problem statements');
    }
  }, [error]);

  useEffect(() => { load(hackathonId); setPreview(null); }, [hackathonId, load]);

  const run = async (fn: () => Promise<any>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const previewOffer = () => run(async () => {
    try {
      const res = await api.post(`/teams/admin/offer-problems/${hackathonId}`, { perTeam, cap, dryRun: true });
      setPreview(res.data.preview);
    } catch (err: any) {
      setPreview(null);
      // The arithmetic refusal is the useful part of this screen, so it is shown
      // in full rather than trimmed to fit a toast.
      error(err.response?.data?.message || 'Could not work out an allocation');
    }
  });

  const commitOffer = () => run(async () => {
    if (!window.confirm(`Offer ${perTeam} problem statements to each unlocked team? Any shortlist they already have is replaced.`)) return;
    try {
      const res = await api.post(`/teams/admin/offer-problems/${hackathonId}`, { perTeam, cap });
      success(res.data.message);
      setPreview(null);
      load(hackathonId);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not offer the problem statements');
    }
  });

  const resetAll = () => run(async () => {
    try {
      const look = await api.post(`/teams/admin/reset-problems/${hackathonId}`, { dryRun: true });
      if (!look.data.affected) { success('No team has a problem statement to take back.'); return; }
      if (!window.confirm(
        `Put ${look.data.affected} team(s) back to having no problem statement?\n\n${look.data.names.join(', ')}\n\nShortlists, choices and assignments are all dropped.`
      )) return;
      const res = await api.post(`/teams/admin/reset-problems/${hackathonId}`);
      success(res.data.message);
      load(hackathonId);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not reset the problem statements');
    }
  });

  const act = (label: string, fn: () => Promise<any>) => run(async () => {
    try {
      const res = await fn();
      success(res.data.message || label);
      load(hackathonId);
    } catch (err: any) {
      error(err.response?.data?.message || `Could not ${label.toLowerCase()}`);
    }
  });

  const withCap = (u: Usage) => u.assignedTo.length + u.chosenBy.length;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label htmlFor="ps-hackathon" className="mb-1 block text-sm font-medium">Hackathon</label>
        <select
          id="ps-hackathon" value={hackathonId} onChange={(e) => setHackathonId(e.target.value)}
          className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2"
        >
          <option value="">Choose a hackathon…</option>
          {hackathons.map((h) => <option key={h._id} value={h._id}>{h.title}</option>)}
        </select>
      </div>

      {hackathonId && (
        <>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="flex items-center gap-2 font-semibold"><BookOpen className="h-4 w-4" /> Offer a shortlist to every team</h3>
            <p className="mt-1 text-xs text-gray-600">
              Each team is offered the same number of problems, and no problem goes to more
              teams than the limit. Whether that is possible at all is arithmetic — preview
              first and it will say so before anything changes. Locked teams are left alone.
            </p>

            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Offered to each team</span>
                <input type="number" min={1} value={perTeam} onChange={(e) => setPerTeam(Number(e.target.value))}
                  className="mt-1 w-24 rounded-md border border-gray-300 px-2 py-1" />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Most teams per problem</span>
                <input type="number" min={1} value={cap} onChange={(e) => setCap(Number(e.target.value))}
                  className="mt-1 w-24 rounded-md border border-gray-300 px-2 py-1" />
              </label>
              <button type="button" disabled={busy} onClick={previewOffer}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">
                Preview
              </button>
              <button type="button" disabled={busy || !preview} onClick={commitOffer}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                Offer to every team
              </button>
              <button type="button" disabled={busy} onClick={resetAll}
                className="ml-auto flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">
                <RotateCcw className="h-3.5 w-3.5" /> Put everyone back to nothing
              </button>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              {teams.length} team(s) × {perTeam} needs {teams.length * perTeam} places;
              {' '}{usage.length} problems × {cap} gives {usage.length * cap}.
            </p>

            {preview && (
              <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-gray-200">
                <table className="w-full text-sm">
                  <tbody>
                    {preview.map((row) => (
                      <tr key={row.team} className="border-b last:border-0">
                        <td className="px-3 py-2 font-medium">{row.team}</td>
                        <td className="px-3 py-2 text-gray-600">{row.statements.join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="font-semibold">How thinly the problems are spread</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500">
                  <tr><th className="py-2">Problem</th><th>Working on it</th><th>Chosen</th><th>Offered to</th></tr>
                </thead>
                <tbody>
                  {usage.map((u) => (
                    <tr key={u.statementId} className="border-t">
                      <td className="py-2 pr-3">{u.title}</td>
                      <td className={withCap(u) > cap ? 'font-semibold text-red-600' : ''}>{u.assignedTo.length}</td>
                      <td>{u.chosenBy.length}</td>
                      <td>{u.offeredTo.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="font-semibold">Where each team stands</h3>
            <div className="mt-3 space-y-2">
              {teams.map((t) => {
                const offered = t.offeredProblemStatements || [];
                const locked = Boolean(t.problemStatementLockedAt);
                return (
                  <div key={t._id} className="rounded-md border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{t.name}</span>
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">{t.status}</span>
                      {locked && (
                        <span className="flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          <Lock className="h-3 w-3" /> locked
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-xs text-gray-600">
                      {t.assignedProblemStatement?.title
                        ? <>Working on <strong>{t.assignedProblemStatement.title}</strong></>
                        : t.selectedProblemStatement?.title
                          ? <>Chose <strong>{t.selectedProblemStatement.title}</strong> — waiting to be locked in</>
                          : offered.length
                            ? <>Offered {offered.length}: {offered.map((o) => o.title).join(' · ')} — has not chosen yet</>
                            : 'Nothing offered yet'}
                    </p>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {t.selectedProblemStatement?.title && !locked && (
                        <button type="button" disabled={busy}
                          onClick={() => act('Lock it in', () => api.post(`/teams/${t._id}/lock-problem`))}
                          className="flex items-center gap-1 rounded-md border border-green-600 px-2 py-1 text-xs text-green-700 hover:bg-green-50 disabled:opacity-50">
                          <Lock className="h-3 w-3" /> Lock it in and approve
                        </button>
                      )}
                      {locked && (
                        <button type="button" disabled={busy}
                          onClick={() => act('Unlock', () => api.post(`/teams/${t._id}/unlock-problem`))}
                          className="flex items-center gap-1 rounded-md border border-amber-500 px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                          <Unlock className="h-3 w-3" /> Unlock
                        </button>
                      )}
                      {offered.length > 0 && !locked && (
                        <button type="button" disabled={busy}
                          onClick={() => act('Withdraw the shortlist', () => api.delete(`/teams/${t._id}/offered-problems`))}
                          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                          Withdraw shortlist
                        </button>
                      )}
                      {t.assignedProblemStatement?.title && (
                        <button type="button" disabled={busy}
                          onClick={() => act('Withdraw the problem statement', () => api.delete(`/teams/${t._id}/assigned-problem`))}
                          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                          Withdraw problem
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!teams.length && <p className="text-sm text-gray-500">No teams in this hackathon yet.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
